<template>
	<div>
		<h3>Simple Mode</h3>
		<b-img src="/images/importer/module-configurable-parts/configurable-simple.jpg"></b-img>
		<p>
			The Configurable Parts Module allows you to set parameters so users can adjust the scale of body parts on your character, or in Customizable mode even swap out genital types entirely.
		</p>
        
		<p>
			Use Simple Mode if you don't have any customizable parts on your character, or if you want some basic scaling support but don't have swappable body parts set up.
		</p>

		<p>
			<span class="font-weight-bold">Has Breasts:</span> Set this flag on if your character has breasts.
		</p>
        
		<p>
			<span class="font-weight-bold">Allow (X) Scaling:</span> Set these flags to enable the scaling slider for the given body part in the Character Menu.
		</p>
        
		<p>
			<span class="font-weight-bold">Default (X) Scale:</span> Set the default value for the scale slider for each body part, when this character species is selected.
		</p>
        
		<p>
			As mentioned in the help text in the module, each of the scalable body parts is driven by an Expression animation assigned in the Expressions module, where the minimum size should be defined
			on the first frame of the animation and the maximum size on the last frame.
		</p>
        
		<h3 class="mt-5">Customizable Mode</h3>
		<b-img src="/images/importer/module-configurable-parts/configurable-customizable.jpg"></b-img>
		<p>
			Use Customizable Mode if your character has multiple body types, genital options, or other cosmetics that you want to be toggleable in the Character Menu.
		</p>

		<p>
			<span class="font-weight-bold">Allow No Genitals:</span> This flag will allow your character to have both Cock and Pussy genitals set to None at the same time. If you want to force your 
			character to have at least one at all times, leave this unchecked.
		</p>
        
		<h4>Configurable Groups</h4>
		<p>
			Define the types of configurable parts that your character will have. Only one group of each type should be defined, with the exception of Cosmetic which can have multiple groups. The 
			group types are:
			<ul>
				<li>Body With Separate Meshes: Use this if you want to swap between two different meshes for the character's body when changing body types.</li>
                <li>Body With Blendshapes: Use this if you want to swap between two different body types using blendshapes defined on the body mesh.</li>
                <li>Sheath: For allowing your character to have both a sheath or male slit option.</li>
                <li>Cock: To support multiple cock types.</li>
                <li>Pussy: To support multiple pussy types.</li>
                <li>Cosmetic: A catch-all for any other configurable parts that don't have a specific impact on gameplay like the other group types.</li>
			</ul>
		</p>

		<p>
			<span class="font-weight-bold">Allow None:</span> On supported group types, check this box to allow all options to be deselected by the user in the Character Menu.
		</p>

		<p>
			<span class="font-weight-bold">Allow Multiple:</span> On supported group types, check this box to allow more than one option to be selected by the user in the Character Menu.
		</p>

		<h4 class="mt-5">Common Configurable Part Fields</h4>
		<p>
			All configurable part types have some fields in common:
		</p>

		<p>
			<span class="font-weight-bold">Part Name:</span> The name of the body part that will appear in the Character Menu.
		</p>

		<p>
			<span class="font-weight-bold">Group Name:</span> The configurable group that this body part belongs to.
		</p>

		<p>
			<span class="font-weight-bold">Is Default Option:</span> Check this box to enable this body part by default when the species is selected in the Character Menu.
		</p>

		<p>
			<span class="font-weight-bold">Include Objects:</span> Select any game objects in your character that should be enabled when this body part is selected. These objects will also be disabled
			if this body part is deselected, unless they are also listed as Include Objects in other selected parts in the same group.
		</p>

		<p>
			<span class="font-weight-bold">Exclude Objects:</span> Select any game objects in your character that should be disabled when this body part is selected. These objects will also be enabled
			if this body part is deselected, unless they are also listed as Include Objects in other selected parts in the same group.
		</p>

		<h4 class="mt-5">Body With Separate Meshes</h4>
		<b-img src="/images/importer/module-configurable-parts/body-types.jpg"></b-img>
		<p>
			Be sure to include your body mesh objects in the Include Objects for each body type.
		</p>

		<p>
			<span class="font-weight-bold">Has Breasts:</span> This flag indicates if this Body Type has breasts, which will show the Breast Size slider for this Body Type if breast scaling is also enabled.
		</p>

		<h4 class="mt-5">Body With Blendshapes</h4>
		<b-img src="/images/importer/module-configurable-parts/blendshape-body-types.jpg"></b-img>
		<p>
			<span class="font-weight-bold">Has Breasts:</span> This flag indicates if this Body Type has breasts, which will show the Breast Size slider for this Body Type if breast scaling is also enabled.
		</p>

		<p>
			<span class="font-weight-bold">Skinned Mesh:</span> Assign the mesh for the character's body that will have its blendshapes changed when changing body types. Currently only one skinned mesh
			renderer is supported.
		</p>
        
		<p>
			<span class="font-weight-bold">Blend Shape Weights:</span> This list is for setting the names and weight values of each blendshape that this body type uses, from a scale of 0 - 100.
		</p>

		<h4 class="mt-5">Sheath Types</h4>
		<b-img src="/images/importer/module-configurable-parts/sheath-types.jpg"></b-img>
		<p>
			<span class="font-weight-bold">Is Slit:</span> This flag indicates if this sheath type should be considered a "slit" for the purposes of available interactions. If unchecked, it will be considered
			a "sheath" instead.
		</p>
        
		<p>
			<span class="font-weight-bold">Has Balls:</span> Indicates whether this sheath type has balls when active, for the purpose of available interactions.
		</p>
        
		<p>
			<span class="font-weight-bold">Unsheath Expression Override:</span> Characters that have both Slit and Sheath genital options would likely require separate unsheathing animations
			to work correctly. Use the regular "Cock Unsheath" expression animation in the Expressions module to act as a default/fallback animation, and set another expression animation here as needed
			for any other sheath types.
		</p>
        
		<p>
			<span class="font-weight-bold">Cock Scale Expression Override:</span> Characters that have both Slit and Sheath genital options may require separate cock scaling animations
			to work correctly. Use the regular "Cock Scale" expression animation in the Expressions module to act as a default/fallback animation, and set another expression animation here as needed
			for any other sheath types.
		</p>

		<p>
			<span class="font-weight-bold">Cock Rotation Base Override:</span> Characters that have both Slit and Sheath genital options may require separate cock rotation base bones
			to work correctly. Use the regular "Cock Rotation Base" in the Cock module to act as a default/fallback bone, and set another bone here as needed for any other sheath types. For example,
			use the Sheath bone when a character has a sheath and set it to the base bone of the cock for a character with a Slit.
		</p>
        
		<h4 class="mt-5">Cock Types</h4>
		<b-img src="/images/importer/module-configurable-parts/cock-types.jpg"></b-img>
		<p>
			<span class="font-weight-bold">Has Knot:</span> This flag indicates if this cock type has a knot that swells during orgasm.
		</p>
        
		<p>
			<span class="font-weight-bold">Has Flare:</span> This flag indicates if this cock type has a flared tip that swells during orgasm.
		</p>
        
		<p>
			<span class="font-weight-bold">Is None Option:</span> This flag indicates that this "cock type" should be handled as not having a cock when selected - disabling actions that require a cock,
			size scaling options, etc.
		</p>

		<h4 class="mt-5">Pussy Types</h4>
		<b-img src="/images/importer/module-configurable-parts/pussy-types.jpg"></b-img>
		<p>
			<span class="font-weight-bold">Allow Hermaphrodite Genitals:</span> If checked, the character is allowed to have both a cock and pussy. Otherwise, their cock type will be set to None if a
			pussy is selected, and vice versa.
		</p>
		<p>
			<span class="font-weight-bold">Is None Option:</span> This flag indicates that this "pussy type" should be handled as not having a pussy when selected - disabling actions that require a pussy, etc.
		</p>

		<h4 class="mt-5">Cosmetic Types</h4>
		<b-img src="/images/importer/module-configurable-parts/cosmetic-types.jpg"></b-img>
		<p>
			There are no special fields for these groups - but since there can be multiple cosmetic group types defined, be sure the "Group Name" field is set to the correct group when defining these
			body part options, if you have set up multiple groups.
		</p>
        
		<h4 class="mt-5">Other Options</h4>
		<p>
			<span class="font-weight-bold">Allow Cock Scaling, Allow Balls Scaling, Allow Breast Scaling, etc:</span> These fields function the same as in Simple Mode - see above.
		</p>
	</div>
</template>

<script>
export default {
		name: 'ConfigurablePartsModule',
}
</script>
